import React, { useState, useCallback } from 'react';
import { useToast } from '@cargomatic/ui';
import { useOrderVendorLineItems } from './hooks/useOrderVendorLineItems';
import { updateVendorPayable } from 'actions/ShipmentsActionCreators';
import ConfirmModal from 'components/ui/modals/ConfirmModal';
import formatMoney from 'utils/formatters/Money';
import VendorPayablesTable from './VendorPayablesTable';
export default function OrderVendorPayables({ orderId, refetchOnMount = 'always', enabled = true, isLegManagementEnabled = false, }) {
    const { data: vendorLineItems = [], isLoading, isFetching, refetch: refetchVendorLineItems, } = useOrderVendorLineItems(orderId, { refetchOnMount, enabled });
    const { toast } = useToast();
    // State for paid toggle confirmation
    const [vendorPayableToToggle, setVendorPayableToToggle] = useState(null);
    const [isUpdating, setIsUpdating] = useState(false);
    const [updatingLineItemId, setUpdatingLineItemId] = useState(null);
    // Handler to initiate paid toggle
    const handleTogglePaid = useCallback((item) => {
        var _a, _b, _c, _d;
        if (!((_a = item.shipment) === null || _a === void 0 ? void 0 : _a._id) || !item.adjustment_id) {
            toast({
                title: 'Error',
                description: 'Cannot update payment status: missing shipment or adjustment ID.',
                variant: 'destructive',
            });
            return;
        }
        setVendorPayableToToggle({
            lineItemId: item.adjustment_id,
            shipmentId: item.shipment._id,
            currentStatus: item.payment_status === 'paid' ? 'paid' : 'unpaid',
            message: (_c = (_b = item.message) !== null && _b !== void 0 ? _b : item.category) !== null && _c !== void 0 ? _c : 'Vendor charge',
            value: (_d = item.value) !== null && _d !== void 0 ? _d : 0,
        });
    }, [toast]);
    // Handler to confirm and execute the toggle
    const handleConfirmToggle = useCallback(async () => {
        if (!vendorPayableToToggle)
            return;
        setIsUpdating(true);
        setUpdatingLineItemId(vendorPayableToToggle.lineItemId);
        try {
            const newStatus = vendorPayableToToggle.currentStatus === 'paid' ? 'unpaid' : 'paid';
            await updateVendorPayable({
                lineItem: vendorPayableToToggle.lineItemId,
                shipmentId: vendorPayableToToggle.shipmentId,
                status: newStatus,
            });
            toast({
                title: 'Success',
                description: `Vendor charge marked as ${newStatus}.`,
            });
            // Refetch vendor line items to get updated data
            refetchVendorLineItems();
        }
        catch (_error) {
            toast({
                title: 'Error',
                description: 'Failed to update vendor payment status.',
                variant: 'destructive',
            });
        }
        finally {
            setIsUpdating(false);
            setUpdatingLineItemId(null);
            setVendorPayableToToggle(null);
        }
    }, [vendorPayableToToggle, refetchVendorLineItems, toast]);
    // Handler to cancel the toggle
    const handleCancelToggle = useCallback(() => {
        setVendorPayableToToggle(null);
    }, []);
    // Avoid showing skeleton forever during background refetches
    const loading = (isLoading || isFetching) && vendorLineItems.length === 0;
    return (React.createElement(React.Fragment, null,
        React.createElement(VendorPayablesTable, { items: vendorLineItems, loading: loading, isLegManagementEnabled: isLegManagementEnabled, onTogglePaid: handleTogglePaid, updatingItemId: updatingLineItemId, isUpdating: isUpdating }),
        React.createElement(ConfirmModal, { dataTestId: "confirm-vendor-paid-toggle", show: Boolean(vendorPayableToToggle), onHide: handleCancelToggle, onConfirm: handleConfirmToggle, title: "Confirm Payment Status Change", message: vendorPayableToToggle
                ? `Are you sure you want to mark "${vendorPayableToToggle.message}" (${formatMoney(vendorPayableToToggle.value)}) as ${vendorPayableToToggle.currentStatus === 'paid' ? 'unpaid' : 'paid'}?`
                : '', affirmative: isUpdating ? 'Updating...' : 'Confirm', primaryDisabled: isUpdating })));
}
